import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import GmModal from 'app/shared/modal/modal';
import { Button } from 'app/shared/button';
import { Icon } from 'assets/icons';
import { useTenant } from 'contexts/tenant';
import { useChecklist } from 'hooks/checklist';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { useDemoService } from 'hooks/users/demo';
import { SubscriptionPurchaseContext } from 'contexts/checkout/subscription';
import { OnboardingCallModalContext } from 'contexts/onboarding/call-modal';
import styles from './call-modal.module.css';

export const ELIGIBLE_TIERS = ['professional', 'enterprise'];

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
const LAST_SHOWN_KEY = (user_id) => `onboarding-call-modal-last-shown-${user_id}`;

const wasShownToday = (user_id) => {
  const last_shown = window.localStorage.getItem(LAST_SHOWN_KEY(user_id));
  if (!last_shown) return false;
  return new Date(last_shown).toDateString() === new Date().toDateString();
};

export const OnboardingCallModal = () => {
  const { id: user_id, tenant_id, email } = useSelector((state) => state.user_data);
  const { tenant } = useTenant();
  const { show_checklist, loading, refresh } = useChecklist();
  const { fetchSubscription } = useSubscriptionService();
  const { bookOnboardingCall } = useDemoService();
  const { setModuleCode, openModal: openSubscriptionModal } = useContext(
    SubscriptionPurchaseContext
  );
  const { show_modal, openModal, closeModal } = useContext(OnboardingCallModalContext);

  const [is_ineligible, setIsIneligible] = useState(false);
  const [is_booking, setIsBooking] = useState(false);
  const [is_booked, setIsBooked] = useState(false);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant_id]);

  useEffect(() => {
    if (loading || !tenant?.created_on || !show_checklist) return;
    if (Date.now() - new Date(tenant.created_on).getTime() < THREE_DAYS_MS) return;
    if (wasShownToday(user_id)) return;

    window.localStorage.setItem(LAST_SHOWN_KEY(user_id), new Date().toISOString());
    openModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, show_checklist, tenant?.id, tenant?.created_on, user_id]);

  const handleBookCall = async () => {
    const { subscriptions: [subscription] = [] } = await fetchSubscription({
      query_string: 'plan_code=campaign&status=active'
    });

    if (!ELIGIBLE_TIERS.includes(subscription?.tier)) {
      setIsIneligible(true);
      return;
    }

    setIsBooking(true);
    const result = await bookOnboardingCall({ data: { metadata: { email, ...tenant } } });
    setIsBooking(false);
    if (!result) return;

    setIsBooked(true);
  };

  const handleClose = () => {
    closeModal();
    setIsIneligible(false);
    setIsBooked(false);
  };

  const handleUpgradePlan = () => {
    setModuleCode('campaign');
    openSubscriptionModal();
    handleClose();
  };

  return (
    <GmModal
      title="Trouble setting up your account?"
      show_title
      show_modal={show_modal}
      onClose={handleClose}
      bodyClassName={styles.modalBody}
    >
      <div className={styles.container}>
        {is_booked ? (
          <>
            <div className={cx(styles.iconBadge, styles.success)}>
              <Icon name="check_circle" width={22} height={22} />
            </div>
            <p className={styles.heading}>Request received</p>
            <p className={styles.description}>
              Your call request has been received. A specialist will reach out to you shortly.
            </p>
            <div className={styles.actions}>
              <Button text="Okay, thanks" type="secondary" onClick={handleClose} />
            </div>
          </>
        ) : is_ineligible ? (
          <>
            <div className={cx(styles.iconBadge, styles.warn)}>
              <Icon name="credit_card" width={22} height={22} />
            </div>
            <p className={styles.heading}>Upgrade to book a call</p>
            <p className={styles.description}>
              Onboarding calls are available on the professional and enterprise plans. Upgrade your
              subscription to get hands-on help setting up your account.
            </p>
            <div className={styles.actions}>
              <Button text="Upgrade Plan" icon_name="payments" onClick={handleUpgradePlan} />
              <Button text="Maybe later" type="secondary" onClick={handleClose} />
            </div>
          </>
        ) : (
          <>
            <div className={styles.iconBadge}>
              <Icon name="calendar" width={22} height={22} />
            </div>
            <p className={styles.heading}>You're almost there</p>
            <p className={styles.description}>
              Book a call with an onboarding specialist and we'll help you get your account fully
              set up.
            </p>
            <div className={styles.actions}>
              <Button
                text="Book onboarding call"
                icon_name="calendar"
                onClick={handleBookCall}
                loading={is_booking}
                disabled={is_booking}
              />
            </div>
          </>
        )}
      </div>
    </GmModal>
  );
};
